import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import AppShell from './shell/AppShell';
import AddCandidateArchiveModal from './AddCandidateArchiveModal';
import CandidateTable from './CandidateTable';
import { API_BASE_URL } from '../config/api';

const API_BASE = API_BASE_URL;

/**
 * CandidateArchive — Full-page archive wrapper for both recruiter and client.
 * Handles data fetching, page shell, and delegates table/filtering to CandidateTable.
 */
const CandidateArchive = ({ mode: modeProp }) => {
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const mode = modeProp || (user.user_type === 'client' ? 'client' : 'recruiter');
  const isClient = mode === 'client';

  const api = useMemo(() => isClient ? {
    archive: '/api/client/archive',
    filterOptions: '/api/client/archive/filter-options',
    savedViews: '/api/client/archive/saved-views',
    pipelines: '/api/client/pipelines',
    candidateData: (id) => `/api/client/candidates/${id}/data`,
    candidateTags: (id) => `/api/client/candidates/${id}/data`,
    deleteCandidate: (id) => `/api/client/candidates/${id}`,
    bulkAdd: (pipelineId) => `/api/pipelines/${pipelineId}/search-candidates`,
  } : {
    archive: `${API_BASE}/api/candidate-archive/internal-archive`,
    filterOptions: `${API_BASE}/api/candidate-archive/internal-archive/filter-options`,
    savedViews: `${API_BASE}/api/candidate-archive/internal-archive/saved-views`,
    pipelines: null,
    candidateData: (id) => `${API_BASE}/api/candidates/${id}/candidate-data`,
    candidateTags: (id) => `${API_BASE}/api/candidates/${id}/tags`,
    deleteCandidate: (id) => `${API_BASE}/api/candidates/${id}`,
    bulkAdd: (pipelineId) => `${API_BASE}/api/pipelines/${pipelineId}/search-candidates`,
  }, [isClient]);

  const HeaderComponent = null; // removed — AppShell handles the top bar

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pipelines, setPipelines] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ referral_sources: [], tags: [], locations: [] });
  const [savedViews, setSavedViews] = useState([]);
  const [showAddCandidate, setShowAddCandidate] = useState(false);
  const [currentFilters, setCurrentFilters] = useState(null);
  const [syncingIds, setSyncingIds] = useState(new Set());

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}` };

  // Load initial metadata
  useEffect(() => {
    const loadMeta = async () => {
      try {
        const foResp = await axios.get(api.filterOptions, { headers });
        setFilterOptions(foResp.data);
        if (!api.pipelines && foResp.data.pipelines) {
          setPipelines(foResp.data.pipelines);
        }
        if (api.pipelines) {
          const pResp = await axios.get(api.pipelines, { headers });
          setPipelines(pResp.data);
        }
        const svResp = await axios.get(api.savedViews, { headers });
        setSavedViews(svResp.data);
      } catch (err) {
        console.error('Error loading metadata:', err);
      }
    };
    loadMeta();
  }, []);

  const loadCandidates = useCallback(async (filters) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.entries(filters || {}).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params.append(key, value);
        }
      });
      const response = await axios.get(`${api.archive}?${params}`, { headers });
      setCandidates(response.data);
    } catch (err) {
      console.error('Error loading candidates:', err);
    } finally {
      setLoading(false);
    }
  }, [api.archive]);

  useEffect(() => { loadCandidates({}); }, []);

  const handleFiltersChange = useCallback((filters) => {
    setCurrentFilters(filters);
    loadCandidates(filters);
  }, [loadCandidates]);

  // Bulk action callbacks
  const handleBulkAddTag = async (tag, candidateIds) => {
    let successCount = 0, errorCount = 0;
    for (const candidateId of candidateIds) {
      try {
        const getResp = await axios.get(api.candidateData(candidateId), { headers });
        const existingTags = getResp.data?.custom_tags || {};
        const tagId = String(tag.id);
        if (existingTags[tagId]) { successCount++; continue; }
        const updatedTags = { ...existingTags, [tagId]: { name: tag.name, color: tag.color || 'blue' } };
        await axios.put(api.candidateTags(candidateId), { custom_tags: updatedTags }, { headers });
        successCount++;
      } catch (err) { errorCount++; }
    }
    alert(`Added "${tag.name}" to ${successCount} candidates${errorCount > 0 ? `. ${errorCount} failed` : ''}`);
    loadCandidates(currentFilters || {});
  };

  const handleBulkAddToPipeline = async (pipelineId, candidateIds) => {
    await axios.post(api.bulkAdd(pipelineId), { candidate_ids: candidateIds }, { headers });
    alert(`Added ${candidateIds.length} candidate(s) to search`);
    loadCandidates(currentFilters || {});
  };

  const handleBulkDelete = async (candidateIds) => {
    let successCount = 0, errorCount = 0;
    for (const candidateId of candidateIds) {
      try {
        await axios.delete(api.deleteCandidate(candidateId), { headers });
        successCount++;
      } catch (err) { errorCount++; }
    }
    alert(`Deleted ${successCount} candidates${errorCount > 0 ? `. ${errorCount} failed` : ''}`);
    loadCandidates(currentFilters || {});
  };

  const handleDeleteCandidate = async (candidateId) => {
    try {
      await axios.delete(api.deleteCandidate(candidateId), { headers });
      loadCandidates(currentFilters || {});
    } catch (err) {
      console.error('Error deleting candidate:', err);
      alert('Failed to delete candidate');
    }
  };

  const handleBulkSyncLinkedIn = async (candidateIds) => {
    // Filter to candidates that have a LinkedIn URL
    const candidatesWithLinkedIn = candidates.filter(c => {
      const id = c.candidate_id || c.id;
      return candidateIds.includes(id) && (c.linkedin_url || c.linkedin);
    });
    const skipped = candidateIds.length - candidatesWithLinkedIn.length;

    if (candidatesWithLinkedIn.length === 0) {
      alert('None of the selected candidates have a LinkedIn URL. Add LinkedIn URLs first.');
      return;
    }

    const confirmMsg = `Sync ${candidatesWithLinkedIn.length} candidate${candidatesWithLinkedIn.length !== 1 ? 's' : ''} from LinkedIn?` +
      (skipped > 0 ? `\n\n${skipped} will be skipped (no LinkedIn URL).` : '') +
      `\n\nFills blank fields and adds new employment/education entries. Existing data is preserved.` +
      `\n\n⚠️ This can take several minutes — each profile is 30-90 seconds to scrape.`;

    if (!window.confirm(confirmMsg)) return;

    let successCount = 0;
    let failCount = 0;
    let emptyCount = 0;
    let totalFirmsCreated = 0;
    let totalFirmsLinked = 0;
    let totalFirmsPossibleDupes = 0;
    const errors = [];

    for (const c of candidatesWithLinkedIn) {
      const id = c.candidate_id || c.id;
      const name = c.name || `Candidate ${id}`;

      // Mark this candidate as syncing (triggers spinner in grid)
      setSyncingIds(prev => {
        const next = new Set(prev);
        next.add(id);
        return next;
      });

      try {
        // Step 1: trigger
        const triggerRes = await axios.post(
          `${API_BASE}/api/candidates/${id}/sync-linkedin-trigger`,
          {},
          { headers }
        );
        const snapshotId = triggerRes.data.snapshot_id;
        if (!snapshotId) {
          failCount++;
          errors.push(`${name}: no snapshot ID returned`);
          continue;
        }

        // Step 2: poll for completion (max 60 × 5s = 5 min per candidate)
        let result = null;
        let attempts = 0;
        while (attempts < 60) {
          await new Promise(r => setTimeout(r, 5000));
          attempts++;

          try {
            const statusRes = await axios.get(
              `${API_BASE}/api/candidates/${id}/sync-linkedin-status?snapshot_id=${snapshotId}`,
              { headers }
            );
            if (statusRes.data.ready) {
              result = statusRes.data;
              break;
            }
            if (statusRes.data.status === 'failed') {
              throw new Error(statusRes.data.error || 'Failed on LinkedIn side');
            }
          } catch (pollErr) {
            // Ignore transient poll errors and continue
          }
        }

        if (!result) {
          failCount++;
          errors.push(`${name}: timed out`);
          continue;
        }
        if (result.empty) {
          emptyCount++;
        } else {
          successCount++;
          // Aggregate firm stats from this candidate's sync
          const synced = result.synced || {};
          totalFirmsCreated += synced.firms_created || 0;
          totalFirmsLinked += synced.firms_linked || 0;
          totalFirmsPossibleDupes += synced.firms_possible_duplicates || 0;
        }
      } catch (err) {
        failCount++;
        const msg = err.response?.data?.detail || err.response?.data?.error || err.message;
        errors.push(`${name}: ${msg}`);
        if (msg && msg.toLowerCase().includes('cooldown')) {
          errors.push('(cooldown active — stopping bulk sync)');
          // Clear remaining syncing markers
          setSyncingIds(new Set());
          break;
        }
      } finally {
        // Remove this candidate from syncing set
        setSyncingIds(prev => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    }

    // Clear any remaining syncing markers as a safety measure
    setSyncingIds(new Set());

    let msg = `Bulk LinkedIn sync complete!\n\n✓ ${successCount} synced with data`;
    if (emptyCount > 0) msg += `\n○ ${emptyCount} returned no data (private/empty profiles)`;
    if (failCount > 0) msg += `\n✗ ${failCount} failed`;
    if (skipped > 0) msg += `\n⊘ ${skipped} skipped (no LinkedIn URL)`;
    
    if (totalFirmsCreated > 0 || totalFirmsLinked > 0) {
      const firmParts = [];
      if (totalFirmsCreated > 0) firmParts.push(`${totalFirmsCreated} new firm${totalFirmsCreated > 1 ? 's' : ''} created`);
      if (totalFirmsLinked > 0) firmParts.push(`${totalFirmsLinked} linked to existing`);
      msg += `\n\nFirms: ${firmParts.join(', ')}`;
    }
    if (totalFirmsPossibleDupes > 0) {
      msg += `\n\n⚠️ ${totalFirmsPossibleDupes} possible firm duplicate${totalFirmsPossibleDupes > 1 ? 's' : ''} detected.\nGo to Firms → Deduplicate to review.`;
    }
    
    if (errors.length > 0) {
      msg += `\n\nErrors:\n${errors.slice(0, 5).join('\n')}`;
      if (errors.length > 5) msg += `\n... and ${errors.length - 5} more`;
    }
    alert(msg);

    loadCandidates(currentFilters || {});
  };

  const handleSaveView = async (name, viewData) => {
    try {
      await axios.post(api.savedViews, { name, ...viewData }, { headers });
      const resp = await axios.get(api.savedViews, { headers });
      setSavedViews(resp.data);
    } catch (err) {
      console.error('Error saving view:', err);
      alert('Failed to save view');
    }
  };

  const handleDeleteView = async (viewId) => {
    try {
      await axios.delete(`${api.savedViews}/${viewId}`, { headers });
      const resp = await axios.get(api.savedViews, { headers });
      setSavedViews(resp.data);
    } catch (err) {
      console.error('Error deleting view:', err);
    }
  };

  const handleReorderViews = async (viewIds) => {
    const reordered = viewIds.map(id => savedViews.find(v => v.id === id)).filter(Boolean);
    setSavedViews(reordered);
    try {
      await axios.put(`${api.savedViews}/reorder`, { viewIds }, { headers });
    } catch (err) {
      console.error('Error reordering views:', err);
      const resp = await axios.get(api.savedViews, { headers });
      setSavedViews(resp.data);
    }
  };

  return (
    <AppShell variant={isClient ? 'client' : 'recruiter'}>
      <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: 'var(--list-page-bg)' }}>
      <CandidateTable
        candidates={candidates}
        loading={loading}
        onFiltersChange={handleFiltersChange}
        filterOptions={filterOptions}
        pipelines={pipelines}
        savedViews={savedViews}
        onSaveView={handleSaveView}
        onDeleteView={handleDeleteView}
        onReorderViews={handleReorderViews}
        onBulkAddToPipeline={handleBulkAddToPipeline}
        onBulkAddTag={handleBulkAddTag}
        onBulkDelete={handleBulkDelete}
        onBulkSyncLinkedIn={handleBulkSyncLinkedIn}
        syncingIds={syncingIds}
        onDeleteCandidate={handleDeleteCandidate}
        mode={mode}
        embedded={false}
        showPipelineFilter={true}
        showQuickFilters={true}
        showTagFilter={true}
        showSavedViews={true}
        showAddButton={true}
        onAddButtonClick={() => setShowAddCandidate(true)}
        exportLabel="all-candidates"
      />

      {showAddCandidate && (
        <AddCandidateArchiveModal
          userType={isClient ? 'client' : 'internal'}
          onClose={() => setShowAddCandidate(false)}
          onSuccess={() => { loadCandidates(currentFilters || {}); setShowAddCandidate(false); }}
        />
      )}
      </div>
    </AppShell>
  );
};

export default CandidateArchive;
